import React, { useCallback, useEffect, useState } from 'react';
import { authedFetch } from './utils/api';
import { color, radius, space } from './ui/theme';
import { Banner, Card, Chip } from './ui/kit';

/**
 * People who no longer work here.
 *
 * They are gone from the payroll, the rota and the assignment lists, and they
 * are still fully present in the record of what they did. For a dismissal over
 * theft that is the point: the attendance and the transactions are the
 * evidence of what happened, and the ledger is built from them. Deleting them
 * would erase the proof rather than the loss.
 *
 * So this screen shows the counts of what was kept, and whether the login was
 * actually closed -- an unlocked account on a dismissal is the thing most
 * worth seeing at a glance.
 */

const TONE = {
  DISMISSED: 'danger', RESIGNED: 'neutral', REDUNDANT: 'warning',
  CONTRACT_ENDED: 'neutral', RETIRED: 'info', DECEASED: 'neutral',
  OTHER: 'neutral',
};

export default function StaffArchive({ notify }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [only, setOnly] = useState('');

  const load = useCallback(async () => {
    setError(null);
    try {
      const q = only ? `?category=${encodeURIComponent(only)}` : '';
      const res = await authedFetch(`/api/staff-archive${q}`);
      if (!res.ok) throw new Error('Could not load the archive.');
      setRows(await res.json());
    } catch (e) { setError(e.message); }
  }, [only]);

  useEffect(() => { load(); }, [load]);

  if (error) return <Banner tone="danger" title="Could not load">{error}</Banner>;

  const openLogins = (rows || []).filter(
    (r) => r.category === 'DISMISSED' && r.login_locked !== true);

  return (
    <div>
      <Card style={{ marginBottom: space(2) }}>
        <h3 style={{ margin: 0, fontSize: 15 }}>Former staff</h3>
        <div style={{ fontSize: 12.5, color: color.textSecondary, marginTop: 2 }}>
          Removed from payroll and every active list. Their attendance and
          transactions are kept &mdash; those records are what the accounts are
          built from, and on a dismissal they are the evidence of what happened.
        </div>
        <div style={{ marginTop: space(1.5) }}>
          <select value={only} onChange={(e) => setOnly(e.target.value)}
                  style={{ padding: '7px 10px', borderRadius: radius.sm,
                           border: `1px solid ${color.border}`, fontSize: 13 }}>
            <option value="">Everybody who has left</option>
            <option value="DISMISSED">Dismissed</option>
            <option value="RESIGNED">Resigned</option>
            <option value="REDUNDANT">Redundant</option>
            <option value="CONTRACT_ENDED">Contract ended</option>
            <option value="RETIRED">Retired</option>
          </select>
        </div>
      </Card>

      {openLogins.length > 0 && (
        <Banner tone="danger" title="A dismissed person still has a login">
          {openLogins.map((r) => r.name).join(', ')}
          {openLogins.length === 1 ? ' has' : ' have'} no locked account on
          record. If they hold one under a different name it is still open.
        </Banner>
      )}

      {rows && rows.length === 0 && (
        <Banner tone="info" title="Nobody in the archive">
          Everyone on record still works here.
        </Banner>
      )}

      {(rows || []).map((r) => (
        <Card key={r.staff_id} style={{ marginBottom: space(1.5) }}>
          <div style={{ display: 'flex', justifyContent: 'space-between',
                        flexWrap: 'wrap', gap: 10 }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>
                {r.name}
                <span style={{ fontWeight: 400, color: color.textSecondary,
                               fontSize: 12.5, marginLeft: 8 }}>
                  {r.employee_id}{r.position ? ` · ${r.position}` : ''}
                </span>
              </div>
              <div style={{ fontSize: 12.5, color: color.textSecondary,
                            marginTop: 2 }}>
                Left {new Date(r.terminated_on).toLocaleDateString('en-GB')}
                {r.hire_date &&
                  ` · joined ${new Date(r.hire_date).toLocaleDateString('en-GB')}`}
                {r.recorded_by && ` · recorded by ${r.recorded_by}`}
              </div>
              <div style={{ fontSize: 13, marginTop: 6 }}>{r.reason}</div>
              <div style={{ fontSize: 11.5, color: color.textMuted, marginTop: 6 }}>
                Kept on record: {r.attendance_records} attendance
                {r.payslips > 0 && `, ${r.payslips} payslip${r.payslips === 1 ? '' : 's'}`}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <Chip tone={TONE[r.category] || 'neutral'}>
                {(r.category || '').replace(/_/g, ' ')}
              </Chip>
              <div style={{ marginTop: 6 }}>
                {r.login_locked === true && <Chip tone="success">Login locked</Chip>}
                {r.login_locked === false && <Chip tone="danger">Login still open</Chip>}
                {r.login_locked == null && (
                  <span style={{ fontSize: 11.5, color: color.textMuted }}>
                    no login found
                  </span>
                )}
              </div>
              {r.rehire_eligible === false && (
                <div style={{ fontSize: 11.5, color: color.danger, marginTop: 6 }}>
                  Not eligible for rehire
                </div>
              )}
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}
